import React, { useState } from "react";
import {
  updatePassword,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router";
import { auth } from "../api/firebase";
import { useAlert } from "../contexts/AlertContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const PasswordChangePage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const user = auth.currentUser;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const providerId = user?.providerData[0]?.providerId;
  const isEmailUser = providerId === "password";

  const handleReauthAndUpdate = async (newPwd: string) => {
    if (!user) return;

    if (isEmailUser) {
      const credential = EmailAuthProvider.credential(user.email!, currentPassword);
      await reauthenticateWithCredential(user, credential);
    } else if (providerId === "google.com") {
      await reauthenticateWithPopup(user, new GoogleAuthProvider());
    } else if (providerId === "apple.com") {
      await reauthenticateWithPopup(user, new OAuthProvider("apple.com"));
    }

    await updatePassword(user, newPwd);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      await handleReauthAndUpdate(newPassword);
      showSuccess("Password updated successfully.");
      navigate("/preferences");
    } catch (err: any) {
      const msg = err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
        ? "Current password is incorrect."
        : err.message || "Failed to update password.";
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-md mx-auto mt-8">
      <h1 className="text-3xl font-bold my-4">Change Password</h1>
      <form className="space-y-4" onSubmit={handleSubmit}>
        {isEmailUser && (
          <div>
            <Label htmlFor="currentPassword">Current Password</Label>
            <Input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
        )}
        {!isEmailUser && (
          <p className="text-sm text-muted-foreground">
            You signed in with {providerId === "google.com" ? "Google" : "Apple"}.
            You will be asked to re-authenticate before changing your password.
          </p>
        )}
        <div>
          <Label htmlFor="newPassword">New Password</Label>
          <Input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="confirmPassword">Confirm New Password</Label>
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
        <Button type="submit" disabled={loading}>
          {loading ? "Updating..." : "Change Password"}
        </Button>
      </form>
    </div>
  );
};

export default PasswordChangePage;
